import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronDown, KeyRound, Lock, LogIn, Mail } from 'lucide-react';
import { AuthLayout } from '@/components/AuthLayout';
import { FormError } from '@/components/form/FormError';
import { TextField } from '@/components/form/TextField';
import { Button } from '@/components/ui/button';
import { useSubmit } from '@/hooks/useSubmit';
import { isOktaEnabled, ssoErrorMessage, startOktaLogin } from '@/security/sso';
import { useAuthStore } from '@/stores/useAuthStore';

const schema = z.object({
  email: z.email('Email invalide'),
  password: z.string().min(1, 'Mot de passe requis'),
});

type LoginForm = z.infer<typeof schema>;

/** Échec de connexion : même message quelle que soit la cause, pour ne pas révéler si le compte existe */
const loginErrorMessage = () => 'Identifiants incorrects';

export function LoginPage() {
  const session = useAuthStore((state) => state.session);
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const location = useLocation();
  const { submit, submitting, error } = useSubmit(loginErrorMessage);
  /** Message transmis par une autre page (ex. mot de passe réinitialisé) */
  const notice = (location.state as { notice?: string } | null)?.notice;
  /** Échec d'une connexion Okta (retour de l'API ou de SsoCallbackPage : /login?sso_error=…) */
  const [searchParams] = useSearchParams();
  const ssoError = ssoErrorMessage(searchParams.get('sso_error'));
  const [redirecting, setRedirecting] = useState(false);
  // Retour depuis Okta par « Précédent » : le navigateur restaure la page telle qu'au départ (bfcache), boutons grisés
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setRedirecting(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);
  /**
   * Avec le SSO, Okta est mis en avant et l'email / mot de passe (secours) replié ; déplié d'emblée si on arrive d'une
   * page du parcours mot de passe (ex. mot de passe réinitialisé)
   */
  const ssoEnabled = isOktaEnabled();
  const [passwordFormOpen, setPasswordFormOpen] = useState(!ssoEnabled || !!notice);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: zodResolver(schema) });

  // Déjà connecté : « / » mène à la page d'accueil (elle dépend aussi des sociétés de l'utilisateur)
  if (session) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = ({ email, password }: LoginForm) =>
    submit(async () => {
      await login(email, password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? '/', { replace: true });
    });

  const passwordForm = (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      <TextField
        id="email"
        label="Email professionnel"
        required
        type="email"
        autoComplete="username"
        placeholder="nom@entreprise.com"
        icon={Mail}
        error={errors.email?.message}
        {...register('email')}
      />

      <div className="space-y-1">
        <TextField
          id="password"
          label="Mot de passe"
          required
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          icon={Lock}
          error={errors.password?.message}
          {...register('password')}
        />
        <div className="text-right">
          <Link to="/forgot-password" className="text-xs text-blue-600 hover:underline">
            Mot de passe oublié ?
          </Link>
        </div>
      </div>

      {notice && !error && (
        <p role="status" className="text-sm text-emerald-700">
          {notice}
        </p>
      )}

      <FormError error={ssoEnabled ? error : (error ?? (submitting ? null : ssoError))} />

      <Button
        type="submit"
        variant={ssoEnabled ? 'outline' : 'default'}
        className="w-full"
        disabled={submitting || redirecting}
      >
        <LogIn className="w-4 h-4" />
        Se connecter
      </Button>
    </form>
  );

  if (!ssoEnabled) {
    return (
      <AuthLayout title="Bienvenue" subtitle="Connectez-vous à votre espace de gestion des heures supplémentaires">
        {passwordForm}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Bienvenue" subtitle="Connectez-vous à votre espace de gestion des heures supplémentaires">
      <div className="space-y-4">
        {/* Départ vers Okta (via l'API) ; le retour passe par /sso/callback */}
        <Button
          type="button"
          className="w-full"
          disabled={submitting || redirecting}
          onClick={() => {
            setRedirecting(true);
            void startOktaLogin();
          }}
        >
          <KeyRound className="w-4 h-4" />
          Se connecter avec Okta
        </Button>

        <FormError error={error || submitting ? null : ssoError} />
      </div>

      <div className="mt-6 space-y-4">
        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-slate-200" />
          <button
            type="button"
            aria-expanded={passwordFormOpen}
            aria-controls="password-login"
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            onClick={() => setPasswordFormOpen((open) => !open)}
          >
            Se connecter avec un email et un mot de passe
            <ChevronDown className={`w-3 h-3 transition-transform ${passwordFormOpen ? 'rotate-180' : ''}`} />
          </button>
          <span className="h-px flex-1 bg-slate-200" />
        </div>
        <div id="password-login" hidden={!passwordFormOpen}>
          {passwordFormOpen && passwordForm}
        </div>
      </div>
    </AuthLayout>
  );
}
